import React, { useState } from 'react';
import {
  X,
  XCircle,
  Plus,
  Search,
  Building2,
  Mail,
  Phone,
  MapPin,
  UserSquare2Icon,
  CheckCircle2,
  Users,
  ChevronRight,
  ShieldCheck,
  ChevronLeft
} from 'lucide-react';
import { Client, AuthorizedPerson } from '../../types/client.types';
import { Skeleton } from '../common/Skeleton';
import { Spinner } from '../common/Spinner';
import { LoadingState } from '../common/LoadingState';
import { useAuthorizedPersons } from '../../hooks/useAuthorizedPersons';
import './ClientSelectionModal.css';

interface ClientSelectionModalProps {
  onClose: () => void;
  onSelect: (client: Client, person: AuthorizedPerson) => void;
  onAddClient: (client: Client) => void;
  clients: Client[];
  selectedClient: Client | null;
  selectedPerson: AuthorizedPerson | null;
  isLoading?: boolean;
  error?: string | null;
  /**
   * Selector "Solicitar DER" al pie. Solo sale si se pasa: es la forma menos
   * intrusiva de preguntarlo cuando la venta va a llevar factura. Marcarlo no
   * pide nada aqui; quien abre el selector pide los datos del viajero despues.
   */
  der?: { checked: boolean; onChange: (value: boolean) => void };
}

export function ClientSelectionModal({
  onClose,
  onSelect,
  onAddClient,
  clients,
  selectedClient: currentClient,
  selectedPerson: currentPerson,
  isLoading,
  error,
  der
}: ClientSelectionModalProps) {
  const [mode, setMode] = useState<'list' | 'create'>('list');
  const [createStep, setCreateStep] = useState<'form' | 'verify'>('form');
  const [searchTerm, setSearchTerm] = useState('');
  const [tempClient, setTempClient] = useState<Client | null>(currentClient);
  const [tempPerson, setTempPerson] = useState<AuthorizedPerson | null>(currentPerson);
  const {
    persons,
    isLoading: isLoadingPersons,
    error: personsError
  } = useAuthorizedPersons(tempClient?.id ?? null);

  const handleSelectTempClient = (client: Client) => {
    setTempClient(client);
    setTempPerson(null);
  };

  // New Client Form State
  const [newClient, setNewClient] = useState({
    companyName: '',
    cif: '',
    email: '',
    phone: '',
    address: ''
  });

  const filteredClients = clients.filter(c =>
    c.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cif.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [isCreating, setIsCreating] = useState(false);

  const handleCreateClient = async () => {
    if (!newClient.companyName || !newClient.cif) return;

    try {
      setIsCreating(true);
      const { createClient } = await import('../../services/ClientService');
      const client = await createClient(
        newClient.companyName,
        newClient.cif,
        newClient.email,
        newClient.phone,
        newClient.address
      );
      
      onAddClient(client);
    } catch (e) {
      console.error('Error creating client:', e);
      alert(e instanceof Error ? e.message : 'Error creando el cliente');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="csm-overlay">
      <div className="csm-modal">
        {/* Header (Always Visible) */}
        <div className="csm-header">
          <div className="csm-header-left">
            <h3 className="csm-header-title">
              {mode === 'list' ? 'Gestión de Clientes' : 'Alta de Nuevo Cliente'}
            </h3>
            <div className="csm-header-divider" />
            <button
              onClick={() => {
                if (mode === 'create') {
                  setMode('list');
                  setCreateStep('form');
                } else {
                  setMode('create');
                }
              }}
              className={`csm-toggle-btn ${mode === 'create' ? 'csm-toggle-btn-list' : 'csm-toggle-btn-create'}`}
              title={mode === 'create' ? 'Volver al listado' : 'Crear nuevo cliente'}
            >
              {mode === 'create' ? <XCircle className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
            </button>
          </div>

          <button onClick={onClose} className="csm-close-btn">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content Area */}
        <div className="csm-content">
          {mode === 'list' ? (
            <>
              {/* List Mode: Split Layout */}
              <div className="csm-list-sidebar">
                <div className="csm-search-container">
                  <div className="csm-search-input-wrapper">
                    <Search className="csm-search-icon" />
                    <input
                      type="text"
                      placeholder="Buscar por Empresa o CIF..."
                      autoFocus
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="csm-search-input"
                    />
                  </div>
                </div>

                <div className="csm-client-list">
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="csm-client-item al-skeleton-row" aria-hidden="true">
                        <Skeleton width="2.5rem" height="2.5rem" radius="0.75rem" />
                        <div className="csm-client-info" style={{ flex: 1 }}>
                          <Skeleton width="80%" height="0.8rem" />
                          <Skeleton width="45%" height="0.7rem" className="csm-skeleton-cif" />
                        </div>
                      </div>
                    ))
                  ) : error ? (
                    <p className="csm-list-error">{error}</p>
                  ) : (
                    filteredClients.map(client => (
                      <button
                        key={client.id}
                        onClick={() => handleSelectTempClient(client)}
                        className={`csm-client-item ${tempClient?.id === client.id ? 'active' : ''}`}
                      >
                        <div className="csm-client-icon-box">
                          <Building2 className="csm-client-icon" />
                        </div>
                        <div className="csm-client-info">
                          <p className="csm-client-name">{client.companyName}</p>
                          <p className="csm-client-cif">{client.cif}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Detail View */}
              <div className="csm-detail-view">
                {tempClient ? (
                  <div key={tempClient.id} className="csm-slide-right" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div className="csm-slide-right-scrollable">
                      <div className="csm-detail-header">
                        <span className="csm-detail-tag">Expediente Verificado</span>
                        <h4 className="csm-detail-title">{tempClient.companyName}</h4>
                        <div className="csm-detail-meta">
                          <p>{tempClient.cif}</p>
                          <span>|</span>
                          <p>{tempClient.id}</p>
                        </div>

                        <div className="csm-detail-grid">
                          <div className="csm-detail-info-item">
                            <div className="csm-info-icon-box"><Mail className="csm-info-icon" /></div>
                            <span className="truncate" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tempClient.email || 'N/D'}</span>
                          </div>
                          <div className="csm-detail-info-item">
                            <div className="csm-info-icon-box"><Phone className="csm-info-icon" /></div>
                            <span>{tempClient.phone || 'N/D'}</span>
                          </div>
                          <div className="csm-detail-info-item col-span-2">
                            <div className="csm-info-icon-box"><MapPin className="csm-info-icon" /></div>
                            <span className="truncate" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tempClient.address || 'N/D'}</span>
                          </div>
                        </div>
                      </div>

                      <div className="csm-detail-persons">
                        <h5 className="csm-persons-title">
                          <UserSquare2Icon className="w-4 h-4" /> Personas Autorizadas
                        </h5>
                        <div className="csm-persons-list">
                          {isLoadingPersons ? (
                            <LoadingState text="Cargando autorizados..." size={24} variant="inline" />
                          ) : personsError ? (
                            <p className="csm-list-error">{personsError}</p>
                          ) : persons.length > 0 ? (
                            persons.map(person => (
                              <button
                                key={person.id}
                                onClick={() => setTempPerson(person)}
                                className={`csm-person-item ${tempPerson?.id === person.id ? 'active' : ''}`}
                              >
                                <div className="csm-person-info-wrap">
                                  <div className="csm-person-avatar">
                                    {person.name.charAt(0)}
                                  </div>
                                  <div className="csm-person-details">
                                    <p className="csm-person-name">{person.name}</p>
                                    <p className="csm-person-dni">DNI: {person.dni}</p>
                                  </div>
                                </div>
                                {tempPerson?.id === person.id && <CheckCircle2 className="csm-person-check" />}
                              </button>
                            ))
                          ) : (
                            <div className="csm-no-persons">
                              <Users className="csm-no-persons-icon" />
                              <p className="csm-no-persons-title">Sin autorizados registrados</p>
                              <p className="csm-no-persons-desc">Este cliente puede realizar compras directas o requiere añadir autorizados en gestión.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="csm-detail-footer">
                      <button
                        disabled={isLoadingPersons || (persons.length > 0 && !tempPerson)}
                        onClick={() => tempClient && (persons.length === 0 || tempPerson) && onSelect(tempClient, tempPerson || { id: 'generic', name: 'Compra Directa', dni: '-' })}
                        className="csm-confirm-btn"
                      >
                        CONFIRMAR SELECCIÓN
                        <ChevronRight className="w-6 h-6" />
                      </button>
                      {persons.length > 0 && !tempPerson && <p className="csm-confirm-warning">⚠️ Seleccione una persona autorizada</p>}
                    </div>
                  </div>
                ) : (
                  <div className="csm-empty-view">
                    <div className="csm-empty-icon-box">
                      <Building2 className="csm-empty-icon" />
                    </div>
                    <h4 className="csm-empty-title">Selección de Empresa</h4>
                    <p className="csm-empty-desc">Selecciona o crea una cuenta de empresa para gestionar facturación y autorizados.</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Create Mode */
            <div className="csm-create-view">
              {createStep === 'form' ? (
                <div key="form" className="csm-form-container csm-slide-up">
                  <div className="csm-form-header">
                    <h4 className="csm-form-title">Información de Empresa</h4>
                    <p className="csm-form-desc">Completa todos los campos obligatorios para activar el perfil de facturación del cliente.</p>
                  </div>

                  <form className="csm-form" onSubmit={(e) => { e.preventDefault(); setCreateStep('verify'); }}>
                    <div className="csm-form-row">
                      <div className="csm-form-group">
                        <label className="csm-label csm-label-req">Razón Social *</label>
                        <input
                          required
                          type="text"
                          placeholder="Ej: Constructora del Norte S.L."
                          className="csm-input csm-input-text csm-input-req"
                          value={newClient.companyName}
                          onChange={(e) => setNewClient({ ...newClient, companyName: e.target.value })}
                        />
                      </div>
                      <div className="csm-form-group">
                        <label className="csm-label csm-label-req">CIF / NIF *</label>
                        <input
                          required
                          type="text"
                          placeholder="B12345678"
                          className="csm-input csm-input-mono csm-input-req"
                          value={newClient.cif}
                          onChange={(e) => setNewClient({ ...newClient, cif: e.target.value.toUpperCase() })}
                        />
                      </div>
                    </div>

                    <div className="csm-form-row">
                      <div className="csm-form-group">
                        <label className="csm-label csm-label-opt">Correo Electrónico</label>
                        <input
                          type="email"
                          placeholder="administracion@empresa.com"
                          className="csm-input csm-input-normal csm-input-opt"
                          value={newClient.email}
                          onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                        />
                      </div>
                      <div className="csm-form-group">
                        <label className="csm-label csm-label-opt">Teléfono de Contacto</label>
                        <input
                          type="tel"
                          placeholder="+34 912 ..."
                          className="csm-input csm-input-normal csm-input-opt"
                          value={newClient.phone}
                          onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="csm-form-group">
                      <label className="csm-label csm-label-opt">Dirección Fiscal</label>
                      <textarea
                        rows={3}
                        placeholder="Calle, Número, C.P., Ciudad"
                        className="csm-textarea csm-input-opt"
                        value={newClient.address}
                        onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                      />
                    </div>

                    <div className="csm-form-actions">
                      <button
                        type="button"
                        onClick={() => setMode('list')}
                        className="csm-btn-cancel"
                      >
                        CANCELAR
                      </button>
                      <button
                        type="submit"
                        className="csm-btn-submit"
                      >
                        CONTINUAR
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div key="verify" className="csm-verify-container csm-pop-in">
                  <div className="csm-verify-icon-box">
                    <ShieldCheck className="csm-verify-icon" />
                  </div>

                  <h4 className="csm-verify-title">Verifica la Información</h4>
                  <p className="csm-verify-desc">Revisa que los datos fiscales sean correctos antes de confirmar el alta en el sistema.</p>

                  <div className="csm-verify-card">
                    <div className="csm-verify-grid">
                      <div>
                        <p className="csm-verify-label">Nombre Comercial</p>
                        <p className="csm-verify-val-company">{newClient.companyName}</p>
                      </div>
                      <div>
                        <p className="csm-verify-label">C.I.F. / N.I.F.</p>
                        <p className="csm-verify-val-cif">{newClient.cif}</p>
                      </div>
                    </div>

                    <div className="csm-verify-divider" />

                    <div className="csm-verify-grid">
                      <div>
                        <p className="csm-verify-label">Contacto Directo</p>
                        <p className="csm-verify-val-text">{newClient.email || 'No proporcionado'}</p>
                        <p className="csm-verify-val-text">{newClient.phone || 'No proporcionado'}</p>
                      </div>
                      <div>
                        <p className="csm-verify-label">Localización Fiscal</p>
                        <p className="csm-verify-val-text">{newClient.address || 'No proporcionada'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="csm-verify-actions">
                    <button
                      onClick={() => setCreateStep('form')}
                      className="csm-btn-edit"
                    >
                      <ChevronLeft className="w-6 h-6" />
                      EDITAR
                    </button>
                    <button
                      onClick={handleCreateClient}
                      disabled={isCreating}
                      className={`csm-btn-confirm ${isCreating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isCreating ? (
                        <>
                          <Spinner size={24} />
                          CREANDO...
                        </>
                      ) : (
                        <>
                          CONFIRMAR Y DAR DE ALTA
                          <CheckCircle2 className="w-8 h-8" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {der && (
          <label className="csm-der-bar">
            <input
              type="checkbox"
              checked={der.checked}
              onChange={(e) => der.onChange(e.target.checked)}
              data-testid="csm-request-der"
            />
            <span>Solicitar DER (devolución de IVA a viajero fuera de la UE)</span>
          </label>
        )}
      </div>
    </div>
  );
}
